import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@design-system';
import { CheckCircle2, Send } from 'lucide-react';
import { insuranceCarrierDefs } from '@shared/insuranceCarrierDefs';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useIsGenworthMode } from '@/features/genworth/hooks/use-genworth-mode';
import { useSendAnonymousPuiCta } from '../hooks/use-pui-cta';
import { SendPuiDialog } from './send-pui-dialog';
import { PuiHistorySection } from './pui-history-section';

const SUPPORTED_CARRIERS = Object.values(insuranceCarrierDefs)
  .filter(carrier => carrier.supportsPUI === true)
  .map(carrier => carrier.name);

interface SendAnonymousPUIButtonProps {
  //custom size and variant for uniformity across parent component CTAs
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export function SendAnonymousPUIButton({
  variant = 'default',
  size = 'sm',
  className,
}: SendAnonymousPUIButtonProps) {
  const { showDialog, handleOpenDialog, handleCloseDialog, availablePolicies, puiHistory } =
    useSendAnonymousPuiCta();
  const { flags } = useFeatureFlags();
  const isGenworthMode = useIsGenworthMode();

  const puiHistoryHoverElement = () => (
    <>
      {puiHistory && puiHistory.length > 0 && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="cursor-pointer">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="max-h-[500px] w-[480px] overflow-y-auto p-4">
            <PuiHistorySection puiHistory={puiHistory} />
          </HoverCardContent>
        </HoverCard>
      )}
    </>
  );

  // Genworth drops the apply/underwriting surface, including anonymous PUIs.
  if (!flags?.anonymousPUI || isGenworthMode) {
    return null;
  }

  return (
    <>
      <div>
        <TooltipProvider>
          <div className="flex items-center gap-2">
            {puiHistoryHoverElement()}
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handleOpenDialog}
                    variant={variant}
                    size={size}
                    className={className}
                  >
                    <Send className="h-4 w-4" />
                    <span>Send Anonymous PUI</span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <p className="">
                    Send anonymized preliminary underwriting inquiries to{' '}
                    <span className="font-semibold">supported carriers:</span>
                  </p>
                  <ul className="list-inside list-disc space-y-0.5">
                    {SUPPORTED_CARRIERS.map(carrier => (
                      <li key={carrier}>{carrier}</li>
                    ))}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <SendPuiDialog
        open={showDialog}
        onClose={handleCloseDialog}
        availablePolicies={availablePolicies}
        puiHistory={puiHistory}
      />
    </>
  );
}
